import { CONDUCT_AXES, CONDUCT_AXIS_COLUMN } from '../../conduct/conduct-types';
import { ConductAxisField } from '../../conduct/ConductAxisField';
import { ConductAxisReadout } from '../../conduct/ConductAxisReadout';
import { axisResolution, useEffectiveConductProfile } from '../../conduct/use-conduct-profile';

import type { PlaybookDto } from '../types';
import type { PlaybookForm } from '../use-playbook-form';

/**
 * Aba «Conduta» do editor de Playbook — Fase 2 «Arquitetura de Conduta e Contexto».
 *
 * Nível INTERMEDIÁRIO da cascata: vence o Tenant, perde da Persona (que é refinamento deste
 * playbook — só entra em cena porque ESTE playbook a aponta via `personaId`).
 *
 * ★ A resolução é consultada COM `playbookId`, então a cadeia inclui a persona deste
 * playbook. É o que permite avisar «sua escolha está sendo sobreposta pela Persona» em vez de
 * mostrar o valor escolhido como se estivesse valendo — a mentira por omissão que produz «a
 * IA ignorou minha configuração».
 *
 * Exporta as DUAS formas (molde `TabTriggers.tsx`): leitura (sem `form`) e edição.
 * Componentes PUROS: não buscam playbook, não montam patch — o patch é centralizado em
 * `buildPatch` do `use-playbook-form`.
 */

function ConductBody({
  playbook,
  form,
  editable,
}: {
  readonly playbook: PlaybookDto;
  readonly form: PlaybookForm | null;
  readonly editable: boolean;
}): JSX.Element {
  const effective = useEffectiveConductProfile(playbook.id);
  const levels = effective.data?.levels;

  return (
    <div className="max-w-2xl">
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Conduta neste playbook</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Ajustes de comportamento que valem <strong>quando este playbook conduz</strong>. Cada eixo
          herda a configuração do tenant até você opinar aqui — e a persona deste playbook, se
          opinar, é mais específica e vence.
        </p>
        {playbook.status !== 'active' ? (
          <p className="mt-2 rounded-lg bg-app-bg px-3 py-2 text-xs text-text-secondary">
            Este playbook está <strong>{playbook.status}</strong>. Enquanto não estiver ativo, o que
            você configurar aqui NÃO afeta a fala da IA.
          </p>
        ) : null}
        {playbook.personaId === null ? (
          <p className="mt-2 text-xs text-text-secondary">
            Este playbook não usa persona, então não há nível mais específico a competir — o que
            você definir aqui vale direto.
          </p>
        ) : null}
      </header>

      <div className="space-y-4">
        {CONDUCT_AXES.map((axis) => {
          const column = CONDUCT_AXIS_COLUMN[axis];
          const value = form === null ? playbook[column] : form.draft[column];
          // Leitura = TEXTO, nunca `<select disabled>` (mesmo contrato do editor de Persona).
          if (form === null || !editable) {
            return (
              <ConductAxisReadout
                key={axis}
                axis={axis}
                level="playbook"
                resolution={axisResolution(effective.data, axis)}
                levels={levels}
                value={value ?? null}
              />
            );
          }
          return (
            <ConductAxisField
              key={axis}
              axis={axis}
              level="playbook"
              resolution={axisResolution(effective.data, axis)}
              levels={levels}
              value={value ?? null}
              onChange={(next) => {
                // Cast seguro: `next` vem SEMPRE do enum daquele eixo (as opções do seletor
                // são os valores do enum) ou é `null` (a opção «Herdar»).
                form.set(column, next as never);
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Leitura — sem `form`, seletores desabilitados, origem visível. */
export function TabConduct({ playbook }: { readonly playbook: PlaybookDto }): JSX.Element {
  return <ConductBody playbook={playbook} form={null} editable={false} />;
}

/** Edição — gated por `playbook-author` no chamador (`PlaybookEditorView`). */
export function TabConductEdit({
  playbook,
  form,
}: {
  readonly playbook: PlaybookDto;
  readonly form: PlaybookForm;
}): JSX.Element {
  return <ConductBody playbook={playbook} form={form} editable />;
}
